import React, { useContext, useEffect } from 'react';
import { func, shape, string } from 'prop-types';
import { capitalize, useMediaQuery } from '@mui/material';
import { useParams } from 'react-router-dom';

// Context
import { DrawerContext } from 'common/drawer/DrawerContext';

// Utils
import { DRAWER_TYPES } from 'utils/constants';
import Wizard from 'common/Wizard';
import {
  openCreateSelfAuthorizationCloseDialog,
  resetWizardData,
} from './store/createNewSelfAuthorizationSlice';

// components
import CardsPanel from './cards-panel/CardsPanel.connector';
import Sidebar from './sidebar/Sidebar.connector';
import MobileDrawer from '../mobile-drawer/MobileDrawer.connector';
import MobileFilterDrawer from './mobile-filter-drawer/MobileFilterDrawer.connector';
import newSelfAuthSteps from './create-new-steps/Steps';
import ProductConfirmationDialog from './product-confirmation-dialog/ProductConfirmationDialog.connector';

const SelfAuthorizations = ({
  currentSelection,
  dialogOpen,
  resetFilters,
  resetSelfAuth,
  setDataList,
  setMobileDrawerOpen,
  setMobileFilterDrawerOpen,
  setSelfAuthorizations,
  setActiveSelfAuthTab,
  setFilters,
  tempSearchTerm,
}) => {
  const lgUp = useMediaQuery(theme => theme.breakpoints.up('lg'), { noSsr: true });
  const { viewBy } = useParams();
  const { setDrawerComponentProps } = useContext(DrawerContext);

  useEffect(() => {
    if (tempSearchTerm) {
      setFilters({ name: 'searchTerm', value: tempSearchTerm });
      setFilters({ name: 'tempSearchTerm', value: '' });
    }
    return () => {
      setDataList([]);
      setSelfAuthorizations({});
      setActiveSelfAuthTab('baseProducts');
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    return () => {
      resetFilters();
      resetSelfAuth();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [viewBy]);

  const handleActivationClick = () => {
    setMobileDrawerOpen(false);
    setDrawerComponentProps({
      Component: props => (
        <Wizard steps={newSelfAuthSteps} title="New Self Authorizations" {...props} />
      ),
      forceCloseSelector: state => state.loadingManagement.createNewSelfAuth.forceClose,
      holdOpenSelector: state => {
        const { hasUnsavedChanges, submittingBatch } = state.loadingManagement.createNewSelfAuth;
        return hasUnsavedChanges || submittingBatch;
      },
      resetWizard: () => resetWizardData(viewBy),
      setCurrentDialogOpen: openCreateSelfAuthorizationCloseDialog,
      type: DRAWER_TYPES.createNewSelfAuth,
    });
  };

  return (
    <>
      <Sidebar />
      {lgUp ? (
        <CardsPanel />
      ) : (
        <>
          <MobileDrawer
            activationClick={handleActivationClick}
            currentSelection={currentSelection}
            filtersClick={() => setMobileFilterDrawerOpen(true)}
            panel={<CardsPanel />}
            type={`${capitalize(viewBy)}s`}
          />
          <MobileFilterDrawer />
        </>
      )}
      {/* without this check currently the dialog opens twice due to empty state of dialog open */}
      {dialogOpen && <ProductConfirmationDialog />}
    </>
  );
};

SelfAuthorizations.propTypes = {
  currentSelection: shape({ nameShort: string }).isRequired,
  dialogOpen: string,
  resetFilters: func.isRequired,
  resetSelfAuth: func.isRequired,
  setActiveSelfAuthTab: func.isRequired,
  setDataList: func.isRequired,
  setFilters: func.isRequired,
  setMobileDrawerOpen: func.isRequired,
  setMobileFilterDrawerOpen: func.isRequired,
  setSelfAuthorizations: func.isRequired,
  tempSearchTerm: string,
};

SelfAuthorizations.defaultProps = {
  dialogOpen: null,
  tempSearchTerm: undefined,
};

export default SelfAuthorizations;
